import { ResizeHub } from "kido/resize";
import { Sniff } from "kido/utils";

import { installRouter, Intro } from "@/engine";

import { loadPkg } from "./cache";
import { App } from "./context";
import { Cursor } from "./cursor";
import { createGpuHud, type GpuHud } from "./gpu/hud";
import { ImageOverlay } from "./image-overlay";
import { LazyVideoManager } from "./lazy-video";
import { initNav } from "./nav";
import { bootstrap, initAppHeight, resetScrollPosition } from "./utils";
import { VideoOverlay } from "./video-overlay";

export class Application {
  private url: string | null;
  private canvasDetached: HTMLCanvasElement | null;
  private hud: GpuHud | null;

  constructor() {
    this.url = location.pathname;
    this.canvasDetached = null;
    this.hud = null;
  }

  async init(): Promise<void> {
    if (!this.url) {
      console.error("[app] No url found.");
      return;
    }

    // set --app-height for viewport sizing (iOS 26+)
    initAppHeight();

    // reset scroll position
    resetScrollPosition();

    // Load cache
    await loadPkg();

    // Bootstrap
    await bootstrap(this.url);

    // Install client router
    installRouter();

    // Init mobile nav
    initNav();

    const isMobile = Sniff.isMobile;

    if (isMobile) {
      // Mobile: remove canvas, ensure native media is visible
      const canvas = document.getElementById("canvas");
      if (canvas) canvas.remove();
      document.body.classList.remove("has-gpu");
    }

    // Create new Intro with Preloader
    new Intro((ready: () => void) => ready());

    if (!isMobile) {
      // Wire canvas visibility based on screen size (desktop only)
      this.wireCanvasVisibility();

      // Create GPU debug HUD (toggle with Ctrl+F) — dev only
      if (import.meta.env.DEV) {
        this.hud = createGpuHud();
      }
    }

    // Create custom cursor (desktop only)
    if (!isMobile) {
      const cursor = new Cursor();
      cursor.init();
      App.cursor = cursor;
    }

    // Create video overlay
    const videoOverlay = new VideoOverlay();
    videoOverlay.init();
    App.videoOverlay = videoOverlay;

    // Create image overlay
    const imageOverlay = new ImageOverlay();
    imageOverlay.init();
    App.imageOverlay = imageOverlay;

    // Lazy video manager — defers off-screen video downloads
    App.lazyVideo = new LazyVideoManager();

    // Wire global keyboard events
    this.globalKeyboardEvents();
  }

  private wireCanvasVisibility(): void {
    const apply = () => {
      const w = window.innerWidth || document.documentElement.clientWidth;
      const isMobile = w < 1025;
      const existing = document.getElementById(
        "canvas"
      ) as HTMLCanvasElement | null;
      if (isMobile) {
        document.body.classList.remove("has-gpu");
        if (existing) {
          this.canvasDetached = existing;
          existing.remove();
        }
      } else {
        if (!App.gpu) return;
        document.body.classList.add("has-gpu");
        if (!existing && this.canvasDetached) {
          const nav = document.getElementById("nav");
          if (nav && nav.parentElement) {
            nav.parentElement.insertBefore(this.canvasDetached, nav);
          } else {
            document.body.appendChild(this.canvasDetached);
          }
          this.canvasDetached = null;
        }
      }
    };

    ResizeHub.add(apply);
    apply();
  }

  private globalKeyboardEvents(): void {
    window.addEventListener(
      "keydown",
      (event: KeyboardEvent) => {
        const key = (event.key ?? "").toLowerCase();

        if (event.ctrlKey && key === "g") {
          const devGrid = document.querySelector<HTMLElement>(".dev-grid");
          devGrid?.classList.toggle("is-active");
          return;
        }

        if (event.ctrlKey && key === "f") {
          this.hud?.toggle();
          return;
        }
      },
      { passive: true }
    );
  }
}
